import { useMemo } from "react";
import { CampoTexto } from "./CampoTexto";
import { CampoSelectCustom } from "./CampoSelectCustom";
import { mascararTelefone, soDigitos } from "../../utils/mascaras";
import { validarTelefone } from "../../utils/validadores";
import { OPCOES_PAISES_TELEFONE, normalizarDdi } from "../../utils/paisesTelefone";
import styles from "./CampoTelefoneDdi.module.css";

/**
 * Telefone com o código do país (DDI) escolhido em um dropdown à esquerda.
 *
 * O DDI e o número seguem separados: cada um tem sua coluna no banco, o que
 * mantém a máscara brasileira funcionando e permite telefone estrangeiro no
 * mesmo cadastro. A máscara de dígitos só é aplicada ao Brasil; nos demais
 * países o número é guardado apenas com dígitos, porque o agrupamento varia.
 */
export interface CampoTelefoneDdiProps {
  rotulo?: string;
  /** Número sem o código do país. */
  valor: string;
  /** Código do país gravado no cadastro ("+55"). */
  ddi: string;
  onChange: (valor: string) => void;
  onChangeDdi: (ddi: string) => void;
  onBlur?: () => void;
  erro?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  /** Rótulo do dropdown; vazio alinha o campo quando o telefone não tem rótulo. */
  rotuloDdi?: string;
  placeholder?: string;
}

/** Fora do Brasil o formato varia por país: guarda só os dígitos. */
export function mascararTelefonePorDdi(ddi: string, valor: string): string {
  return normalizarDdi(ddi) === "+55" ? mascararTelefone(valor) : soDigitos(valor).slice(0, 15);
}

export function CampoTelefoneDdi({
  rotulo,
  valor,
  ddi,
  onChange,
  onChangeDdi,
  onBlur,
  erro,
  required,
  disabled,
  className,
  rotuloDdi = "País",
  placeholder,
}: CampoTelefoneDdiProps) {
  const ddiAtual = normalizarDdi(ddi);
  const opcoes = useMemo(
    () => OPCOES_PAISES_TELEFONE.map((pais) => ({
      valor: pais.ddi,
      rotulo: `${pais.bandeira} ${pais.ddi}`,
      detalhe: pais.nome,
      busca: `${pais.nome} ${pais.ddi}`,
    })),
    []
  );
  const brasileiro = ddiAtual === "+55";

  return (
    <div className={[styles.grupo, className].filter(Boolean).join(" ")}>
      <CampoSelectCustom
        className={styles.ddi}
        rotulo={rotuloDdi}
        valor={ddiAtual}
        opcoes={opcoes}
        disabled={disabled}
        placeholderBusca="País ou código"
        onChange={(novo) => {
          onChangeDdi(normalizarDdi(novo));
          /* Trocar de país mantém o número digitado, mas reaplica a máscara:
             sair do Brasil tira os parênteses, voltar recoloca. */
          onChange(mascararTelefonePorDdi(novo, valor));
        }}
      />
      <CampoTexto
        className={styles.telefone}
        rotulo={rotulo}
        required={required}
        disabled={disabled}
        erro={erro}
        inputMode="tel"
        placeholder={placeholder ?? (brasileiro ? "(00) 00000-0000" : "Somente números")}
        value={valor}
        valido={brasileiro ? validarTelefone(valor) : soDigitos(valor).length >= 6}
        onChange={(evento) => onChange(mascararTelefonePorDdi(ddiAtual, evento.target.value))}
        onBlur={onBlur}
      />
    </div>
  );
}
